import React, { useState, useRef, useEffect } from "react";
import { Box, Input, InputGroup, Button, Text, Spinner, useToast } from "@chakra-ui/react";
import { sendForgotPasswordCode } from "auth/gridAuthClient";
import ForgotPasswordVerification from "./ForgotPasswordVerification";

const FIELD_W = "260px";
const inputSt = { 
  bg: "transparent", border: "1px solid #fff", borderRadius: "10px", 
  color: "#fff", fontSize: "13px", p: "10px", w: "100%" 
};

export default function ForgotPassword({ onBack }) {
  const toast = useToast();
  const isMountedRef = useRef(true);
  const [state, setState] = useState({
    email: "", error: "", isLoading: false, showVerification: false
  });

  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  useEffect(() => () => { isMountedRef.current = false; }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    updateState({ error: "" });
    
    if (!state.email) return isMountedRef.current && updateState({ error: "Email is required." });
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
      return isMountedRef.current && updateState({ error: "Please enter a valid email address." });
    }

    if (isMountedRef.current) updateState({ isLoading: true });
    
    try {
      const res = await sendForgotPasswordCode(state.email);
      
      if (res?.status === "200" || res?.status === 200 || res?.response) {
        if (isMountedRef.current) updateState({ showVerification: true, isLoading: false });
      } else throw new Error(res?.errorMessage || "Failed to send verification code");
    } catch (err) {
      if (isMountedRef.current) {
        updateState({ error: err.message || "Failed to send reset email. Please try again.", isLoading: false });
      }
    }
  };

  if (state.showVerification) {
    return (
      <ForgotPasswordVerification
        email={state.email}
        onBack={() => updateState({ showVerification: false, error: "" })}
        onSuccess={() => {
          toast({
            title: "Password successfully changed!",
            status: "success",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
          onBack();
        }}
      />
    );
  }

  return (
    <Box as="form" onSubmit={handleSubmit} display="flex" flexDirection="column" alignItems="center" px="25px">
      <Text color="#fff" fontSize="26px" mb="6px">Forgot Password</Text>
      <Text color="#ADA8C3" fontSize="12px" mb="28px" textAlign="center">
        Please enter your email to send a verification code
      </Text>

      <InputGroup w={FIELD_W} mb="18px">
        <Input {...inputSt} type="email" placeholder="Email *" required 
          value={state.email} onChange={e => updateState({ email: e.target.value })} />
      </InputGroup>

      {state.error && (
        <Text color="red" fontSize="12px" mt="8px" textAlign="center" fontWeight="bold">
          {state.error}
        </Text>
      )}

      {state.isLoading ? (
        <Spinner color="white" mt="15px" w="30px" h="30px" thickness="4px" speed="0.65s" />
      ) : (
        <Button type="submit" w={FIELD_W} mt="15px" p="10px" bg="#fff" color="#000" borderRadius="10px"
          disabled={!state.email || state.isLoading}
          _hover={{ cursor: "pointer", bg: "transparent", color: "#fff", outline: "1px solid #fff" }}>
          Send
        </Button>
      )}

      <Text fontSize="12px" color="#fff" mt="15px" textAlign="center">
        <Text as="span" fontWeight="bold" color="#fff" cursor="pointer"
          onClick={onBack} _hover={{ opacity: 0.8 }}>
          Back to Login
        </Text>
      </Text>
    </Box>
  );
}